// ═══════════════════════════════════════════════════════════════════════════
// ShiftBlockModal — Create / edit multi-segment shift blocks
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SEGMENT_TYPES, BLOCK_TYPES, PAY_TYPES, BUILT_IN_TEMPLATES,
  createSegment, createShiftBlock, getBlockWorkingHours, getBlockTimeSpan,
} from '../utils/shiftTypes';
import { calculateBlockPay } from '../utils/payRules';
import { validateShiftBlock, getSectorConfig } from '../utils/schedulingEngine';

const getStaffAssignmentId = (staffMember) => (
  staffMember?.assignmentId ||
  staffMember?.uid ||
  staffMember?.userId ||
  staffMember?.authUid ||
  staffMember?.staffId ||
  staffMember?.id ||
  null
);

const SEGMENT_COLOURS = {
  [SEGMENT_TYPES.STANDARD]: 'bg-blue-600',
  [SEGMENT_TYPES.SLEEP_IN]: 'bg-indigo-500',
  [SEGMENT_TYPES.ON_CALL]: 'bg-amber-500',
  [SEGMENT_TYPES.BREAK]: 'bg-gray-400',
};

const SEGMENT_LABELS = {
  [SEGMENT_TYPES.STANDARD]: 'Standard',
  [SEGMENT_TYPES.SLEEP_IN]: 'Sleep-in',
  [SEGMENT_TYPES.ON_CALL]: 'On-call',
  [SEGMENT_TYPES.BREAK]: 'Break',
};

const PAY_TYPE_LABELS = {
  [PAY_TYPES.STANDARD]: 'Standard',
  [PAY_TYPES.OVERTIME]: 'Overtime',
  [PAY_TYPES.NIGHT_PREMIUM]: 'Night Premium',
  [PAY_TYPES.WEEKEND]: 'Weekend',
  [PAY_TYPES.BANK_HOLIDAY]: 'Bank Holiday',
  [PAY_TYPES.SLEEP_IN_FLAT]: 'Sleep-in (Flat)',
  [PAY_TYPES.SLEEP_IN_DISTURBANCE]: 'Sleep-in (Disturbance)',
  [PAY_TYPES.ON_CALL_STANDBY]: 'On-call Standby',
  [PAY_TYPES.ON_CALL_CALLOUT]: 'On-call Callout',
};

export default function ShiftBlockModal({
  isOpen,
  onClose,
  onSave,
  existingShift,
  date,
  staff,
  groups,
  locations,
  payRules,
  sectorConfig,
  templates: customTemplates,
}) {
  const { t } = useTranslation();
  const isEdit = !!existingShift;

  const [blockType, setBlockType] = useState(BLOCK_TYPES.SINGLE);
  const [segments, setSegments] = useState([
    createSegment(SEGMENT_TYPES.STANDARD, '09:00', '17:00'),
  ]);
  const [shiftDate, setShiftDate] = useState(date || new Date().toISOString().split('T')[0]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [requiredRole, setRequiredRole] = useState('');
  const [notes, setNotes] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [validation, setValidation] = useState(null);
  const span = useMemo(() => getBlockTimeSpan(segments), [segments]);

  // Populate from existing shift
  useEffect(() => {
    if (existingShift) {
      const existingAssignedStaff = (staff || []).find(s =>
        getStaffAssignmentId(s) === existingShift.assignedStaffId || s.id === existingShift.assignedStaffId
      );
      setBlockType(existingShift.blockType || BLOCK_TYPES.SINGLE);
      setSegments(existingShift.segments || [
        createSegment(SEGMENT_TYPES.STANDARD, existingShift.startTime, existingShift.endTime),
      ]);
      setShiftDate(existingShift.date || date);
      setSelectedStaffId(getStaffAssignmentId(existingAssignedStaff) || existingShift.assignedStaffId || '');
      setSelectedGroupId(existingShift.groupId || '');
      setSelectedLocationId(existingShift.locationId || '');
      setRequiredRole(existingShift.requiredRole || '');
      setNotes(existingShift.notes || '');
    }
  }, [existingShift, date, staff]);

  // Live validation
  const config = useMemo(() => getSectorConfig(sectorConfig?.sector || 'general', sectorConfig), [sectorConfig]);

  useEffect(() => {
    const block = { blockType, segments, date: shiftDate };
    setValidation(validateShiftBlock(block, config));
  }, [segments, blockType, config, shiftDate]);

  // Pay preview
  const payPreview = useMemo(() => {
    if (!payRules) return null;
    try {
      return calculateBlockPay(segments, payRules, shiftDate);
    } catch { return null; }
  }, [segments, payRules, shiftDate]);

  // Working hours
  const workingHours = useMemo(
    () => getBlockWorkingHours(segments, config.sleepInCountsAsWorkingTime === true),
    [config.sleepInCountsAsWorkingTime, segments],
  );
  const totalHours = span.spanHours;

  // ── Segment CRUD ──
  const addSegment = (type = SEGMENT_TYPES.STANDARD) => {
    const lastSeg = segments[segments.length - 1];
    const startTime = lastSeg?.endTime || '09:00';
    const startMin = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
    const endMin = startMin + (type === SEGMENT_TYPES.BREAK ? 30 : type === SEGMENT_TYPES.SLEEP_IN ? 480 : 240);
    const endH = Math.floor(endMin / 60) % 24;
    const endM = endMin % 60;
    const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
    setSegments([...segments, createSegment(type, startTime, endTime)]);
  };

  const updateSegment = (index, field, value) => {
    const updated = [...segments];
    updated[index] = { ...updated[index], [field]: value };
    setSegments(updated);
  };

  const removeSegment = (index) => {
    if (segments.length <= 1) return;
    setSegments(segments.filter((_, i) => i !== index));
  };

  const moveSegment = (index, direction) => {
    const arr = [...segments];
    const swap = index + direction;
    if (swap < 0 || swap >= arr.length) return;
    [arr[index], arr[swap]] = [arr[swap], arr[index]];
    setSegments(arr);
  };

  // ── Template application ──
  const applyTemplate = (template) => {
    setBlockType(template.blockType);
    setSegments(template.segments.map(s => ({ ...s })));
    setShowTemplates(false);
  };

  const allTemplates = [
    ...(customTemplates || []),
    ...BUILT_IN_TEMPLATES,
  ];

  // ── Save ──
  const handleSave = () => {
    const block = createShiftBlock({ blockType, segments, date: shiftDate });
    const selectedStaff = staff?.find(s => getStaffAssignmentId(s) === selectedStaffId || s.id === selectedStaffId);
    const assignmentId = selectedStaffId ? getStaffAssignmentId(selectedStaff || { id: selectedStaffId }) : null;
    const assignmentName = selectedStaff?.displayName || selectedStaff?.username || null;
    const data = {
      ...block,
      startTime: span.startTime,
      endTime: span.endTime,
      assignedStaffId: assignmentId,
      assignedStaffName: assignmentName,
      assignedTo: assignmentId,
      assignedToName: assignmentName,
      groupId: selectedGroupId || null,
      locationId: selectedLocationId || null,
      requiredRole: requiredRole || null,
      notes,
      status: assignmentId ? 'assigned' : 'unassigned',
    };
    if (existingShift?.id) data.id = existingShift.id;
    onSave(data);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h2 className="text-lg font-bold text-white">
            {isEdit ? t('shiftBlock.editTitle', 'Edit Shift Block') : t('shiftBlock.createTitle', 'Create Shift Block')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">&times;</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Date + Block Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('shiftBlock.date', 'Date')}</label>
              <input type="date" value={shiftDate} onChange={e => setShiftDate(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('shiftBlock.blockType', 'Block Type')}</label>
              <select value={blockType} onChange={e => setBlockType(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm">
                <option value={BLOCK_TYPES.SINGLE}>{t('shiftBlock.single', 'Single')}</option>
                <option value={BLOCK_TYPES.LINKED}>{t('shiftBlock.linked', 'Linked (Multi-part)')}</option>
                <option value={BLOCK_TYPES.SPLIT}>{t('shiftBlock.split', 'Split Shift')}</option>
              </select>
            </div>
          </div>

          {/* Template Picker */}
          <div>
            <button onClick={() => setShowTemplates(!showTemplates)}
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
              📋 {t('shiftBlock.applyTemplate', 'Apply Template')}
            </button>
            {showTemplates && (
              <div className="mt-2 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {allTemplates.map((tmpl, i) => (
                  <button key={i} onClick={() => applyTemplate(tmpl)}
                    className="text-left p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm border border-gray-600">
                    <div className="font-medium text-white">{tmpl.name}</div>
                    <div className="text-xs text-gray-400">
                      {tmpl.segments.length} segment{tmpl.segments.length > 1 ? 's' : ''} · {tmpl.sector || 'General'}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Segments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-300">{t('shiftBlock.segments', 'Segments')}</label>
              <div className="flex gap-1">
                {Object.entries(SEGMENT_LABELS).map(([type, label]) => (
                  <button key={type} onClick={() => addSegment(type)}
                    className={`px-2 py-1 rounded text-xs text-white ${SEGMENT_COLOURS[type]} hover:opacity-80`}>
                    + {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Timeline visualisation */}
            <div className="flex h-3 rounded overflow-hidden mb-3 bg-gray-900">
              {segments.map((seg, i) => {
                const s = parseInt(seg.startTime?.split(':')[0] || 0);
                const e = parseInt(seg.endTime?.split(':')[0] || 24);
                const width = Math.max(((e > s ? e - s : 24 - s + e) / 24) * 100, 4);
                return (
                  <div key={i} className={`${SEGMENT_COLOURS[seg.type]} h-full`}
                    style={{ width: `${width}%` }} title={`${SEGMENT_LABELS[seg.type]} ${seg.startTime}–${seg.endTime}`} />
                );
              })}
            </div>

            {/* Segment detail rows */}
            <div className="space-y-2">
              {segments.map((seg, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-gray-700/60 rounded-lg border border-gray-600/50">
                  <div className={`w-2 h-8 rounded ${SEGMENT_COLOURS[seg.type]}`} />

                  <select value={seg.type} onChange={e => updateSegment(i, 'type', e.target.value)}
                    className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1 w-24">
                    {Object.entries(SEGMENT_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>

                  <input type="time" value={seg.startTime} onChange={e => updateSegment(i, 'startTime', e.target.value)}
                    className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1 w-24" />
                  <span className="text-gray-500 text-xs">→</span>
                  <input type="time" value={seg.endTime} onChange={e => updateSegment(i, 'endTime', e.target.value)}
                    className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1 w-24" />

                  <select value={seg.payType || PAY_TYPES.STANDARD} onChange={e => updateSegment(i, 'payType', e.target.value)}
                    className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1 flex-1">
                    {Object.entries(PAY_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>

                  {seg.type === SEGMENT_TYPES.SLEEP_IN && (
                    <input type="number" placeholder="Flat £" value={seg.flatRate || ''}
                      onChange={e => updateSegment(i, 'flatRate', parseFloat(e.target.value) || 0)}
                      className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1 w-16" />
                  )}

                  <div className="flex gap-0.5">
                    <button onClick={() => moveSegment(i, -1)} className="text-gray-400 hover:text-white text-xs px-1">▲</button>
                    <button onClick={() => moveSegment(i, 1)} className="text-gray-400 hover:text-white text-xs px-1">▼</button>
                    <button onClick={() => removeSegment(i)} className="text-red-400 hover:text-red-300 text-xs px-1"
                      disabled={segments.length <= 1}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Assignment */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('shiftBlock.staff', 'Assign Staff')}</label>
              <select value={selectedStaffId} onChange={e => setSelectedStaffId(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm">
                <option value="">{t('shiftBlock.unassigned', '— Unassigned —')}</option>
                {(staff || []).map(s => (
                  <option key={s.id} value={getStaffAssignmentId(s) || s.id}>{s.displayName || s.username}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('shiftBlock.role', 'Required Role')}</label>
              <input type="text" value={requiredRole} onChange={e => setRequiredRole(e.target.value)}
                placeholder="e.g. carer, nurse"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('shiftBlock.group', 'Group')}</label>
              <select value={selectedGroupId} onChange={e => setSelectedGroupId(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm">
                <option value="">— None —</option>
                {(groups || []).map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('shiftBlock.location', 'Location')}</label>
              <select value={selectedLocationId} onChange={e => setSelectedLocationId(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm">
                <option value="">— None —</option>
                {(locations || []).map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">{t('shiftBlock.notes', 'Notes')}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm" />
          </div>

          {/* Summary bar */}
          <div className="flex items-center justify-between bg-gray-900 rounded-lg p-3 text-sm">
            <div className="flex gap-4 text-gray-300">
              <span>⏱ {t('shiftBlock.working', 'Working')}: <strong className="text-white">{workingHours.toFixed(1)}h</strong></span>
              <span>📐 {t('shiftBlock.total', 'Total')}: <strong className="text-white">{totalHours.toFixed(1)}h</strong></span>
              <span>🧩 {segments.length} {t('shiftBlock.segmentsLabel', 'segments')}</span>
            </div>
            {payPreview && (
              <span className="text-green-400 font-medium">
                {payPreview.currency || '£'}{payPreview.totalPay?.toFixed(2) || '—'}
              </span>
            )}
          </div>

          {/* Validation messages */}
          {validation && (validation.errors.length > 0 || validation.warnings.length > 0) && (
            <div className="space-y-1">
              {validation.errors.map((e, i) => (
                <div key={`e-${i}`} className="text-red-400 text-sm flex items-start gap-1">
                  <span>❌</span><span>{e}</span>
                </div>
              ))}
              {validation.warnings.map((w, i) => (
                <div key={`w-${i}`} className="text-amber-400 text-sm flex items-start gap-1">
                  <span>⚠️</span><span>{w}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t border-gray-700">
          <button onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white text-sm">
            {t('common.cancel', 'Cancel')}
          </button>
          <button onClick={handleSave}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
            {isEdit ? t('common.save', 'Save') : t('shiftBlock.create', 'Create Shift')}
          </button>
        </div>
      </div>
    </div>
  );
}
